import {
  RowMode,
  type ImportRow as HeldPayComponentRow,
  type PatchImportRow,
} from "../../generated/core-api";
import type { ComponentResolutionMap } from "./buildImportRows";

export type { HeldPayComponentRow };

/**
 * Map a single held `pay_components` canonical row — the generated **read**-side
 * `ImportRow`, as returned inside `EntityRecordView.canonical_entities` from
 * `GET …/workloads/{workloadId}/entities/pay_components` — to its PATCH-only
 * strict counterpart `PatchImportRow` (LADR-039 #440 Step 11, W3b).
 *
 * Aliased to `HeldPayComponentRow` in this module's public signatures: this
 * module's own `buildImportRows.ts` already exports an unrelated **local**
 * `ImportRow` (the Track 1 commit wire-row shape, built from CSV text — no
 * `rowIndex`, `rowMode` required not optional); importing the generated
 * `ImportRow` under its own name here would collide with that existing export
 * for any caller that needs both.
 *
 * `PatchImportRow` is structurally identical to the generated `ImportRow`
 * field-for-field (see both docstrings in `src/generated/core-api.ts`) except
 * for `extra="forbid"` — mapped explicitly here (rather than a cast/spread),
 * mirroring `toPatchWorkPatternRow` in
 * `src/work-patterns/hooks/WorkPatternImport/useWorkPatternImportWorkloadsManager.ts`
 * (Step 8), so the one place the two shapes could diverge in a future API
 * change is this function, not every call site.
 *
 * `rowMode` defaults to `RowMode.Create` only when the held row doesn't carry
 * one at all — a defensive fallback for a first-touch row; a row already
 * committed in a prior run is expected to come back `RowMode.Update` from the
 * server and that value is preserved as-is. This is the explicit Step 11/12
 * default-mode decision called for in `.plans/ladr-039-contract-mapping.md`'s
 * pay-components W3b note (made in code, not silently inferred).
 */
export function toPatchImportRow(row: HeldPayComponentRow): PatchImportRow {
  return {
    rowIndex: row.rowIndex,
    companyCode: row.companyCode,
    employeeCode: row.employeeCode,
    componentName: row.componentName,
    companyComponentId: row.companyComponentId,
    columns: row.columns,
    startDate: row.startDate,
    endDate: row.endDate,
    rowMode: row.rowMode ?? RowMode.Create,
  };
}

/**
 * Stamp `companyComponentId` onto held `pay_components` canonical rows for
 * every component name the PS user just resolved in Map Columns (LADR-039
 * #440 Step 11, W3b — see `.plans/ladr-039-contract-mapping.md`'s pay
 * components vertical) and return the **full** `PatchImportRow[]` ready to
 * `PATCH …/workloads/{workloadId}/entities/pay_components` back.
 *
 * `EntityRecordPatch.canonical_entities` is a full-list **replace**, never a
 * delta, so every held row is carried forward here — not just the ones whose
 * ambiguity was just resolved. A row is only touched when its `componentName`
 * has a *resolved* entry in `resolutions` (an ambiguous name the user has
 * picked an instance for, i.e. `ComponentResolution.companyComponentId` is
 * set); a row whose name isn't ambiguous, or is ambiguous but still
 * unresolved, keeps whatever `companyComponentId` it already carries (usually
 * none yet, unless a prior PATCH already stamped it).
 */
export function stampPayComponentAmbiguityResolutions(
  heldRows: HeldPayComponentRow[],
  resolutions: ComponentResolutionMap,
): PatchImportRow[] {
  return heldRows.map((row) => {
    const patched = toPatchImportRow(row);
    const resolvedId = resolutions[row.componentName]?.companyComponentId;
    if (resolvedId) {
      patched.companyComponentId = resolvedId;
    }
    return patched;
  });
}
